/**
 * Pronunciation Scoring Utility
 * 
 * Calculates a pronunciation accuracy score (0-100) by comparing
 * the recognized speech text against the expected word using both
 * phonetic similarity and string-level similarity.
 * 
 * Approach:
 * - Phonetic comparison via Double Metaphone algorithm (60% weight)
 * - Levenshtein string similarity (40% weight)
 * - Combined score clamped to 0-100
 */

// ============================================================
// Double Metaphone Implementation (simplified for common languages)
// ============================================================

/**
 * Generate Double Metaphone encoding for a word.
 * Returns [primary, alternate] codes.
 * This is a simplified implementation optimized for the
 * vocabulary words used in language learning (short, common words).
 */
export function doubleMetaphone(word: string): [string, string] {
  if (!word || word.length === 0) return ['', ''];

  const input = word.toUpperCase().trim();
  let primary = '';
  let alternate = '';
  let pos = 0;
  const length = input.length;

  // Helper to get char at position
  const charAt = (p: number): string => (p >= 0 && p < length ? input[p] : '');
  const isVowel = (ch: string): boolean => 'AEIOU'.includes(ch);

  // Skip silent initial letters
  if (['GN', 'KN', 'PN', 'AE', 'WR'].some(prefix => input.startsWith(prefix))) {
    pos = 1;
  }

  // Initial X -> S
  if (charAt(0) === 'X') {
    primary += 'S';
    alternate += 'S';
    pos = 1;
  }

  while (pos < length && (primary.length < 4 || alternate.length < 4)) {
    const ch = charAt(pos);

    // Skip vowels unless at start
    if (isVowel(ch)) {
      if (pos === 0) {
        primary += 'A';
        alternate += 'A';
      }
      pos++;
      continue;
    }

    switch (ch) {
      case 'B':
        primary += 'P';
        alternate += 'P';
        pos += charAt(pos + 1) === 'B' ? 2 : 1;
        break;

      case 'C':
        if (['I', 'E', 'Y'].includes(charAt(pos + 1))) {
          primary += 'S';
          alternate += 'S';
        } else {
          primary += 'K';
          alternate += 'K';
        }
        pos += charAt(pos + 1) === 'C' ? 2 : 1;
        break;

      case 'D':
        if (charAt(pos + 1) === 'G' && ['I', 'E', 'Y'].includes(charAt(pos + 2))) {
          primary += 'J';
          alternate += 'J';
          pos += 3;
        } else {
          primary += 'T';
          alternate += 'T';
          pos += charAt(pos + 1) === 'D' ? 2 : 1;
        }
        break;

      case 'F':
        primary += 'F';
        alternate += 'F';
        pos += charAt(pos + 1) === 'F' ? 2 : 1;
        break;

      case 'G':
        if (charAt(pos + 1) === 'H') {
          if (pos + 2 < length && !isVowel(charAt(pos + 2))) {
            pos += 2;
          } else {
            primary += 'K';
            alternate += 'K';
            pos += 2;
          }
        } else if (['I', 'E', 'Y'].includes(charAt(pos + 1))) {
          primary += 'J';
          alternate += 'K';
          pos += 2;
        } else {
          primary += 'K';
          alternate += 'K';
          pos += charAt(pos + 1) === 'G' ? 2 : 1;
        }
        break;

      case 'H':
        if (isVowel(charAt(pos + 1)) && !isVowel(charAt(pos - 1))) {
          primary += 'H';
          alternate += 'H';
        }
        pos++;
        break;

      case 'J':
        primary += 'J';
        alternate += 'H';
        pos++;
        break;

      case 'K':
        primary += 'K';
        alternate += 'K';
        pos += charAt(pos + 1) === 'K' ? 2 : 1;
        break;

      case 'L':
        primary += 'L';
        alternate += 'L';
        pos += charAt(pos + 1) === 'L' ? 2 : 1;
        break;

      case 'M':
        primary += 'M';
        alternate += 'M';
        pos += charAt(pos + 1) === 'M' ? 2 : 1;
        break;

      case 'N':
        primary += 'N';
        alternate += 'N';
        pos += charAt(pos + 1) === 'N' ? 2 : 1;
        break;

      case 'P':
        if (charAt(pos + 1) === 'H') {
          primary += 'F';
          alternate += 'F';
          pos += 2;
        } else {
          primary += 'P';
          alternate += 'P';
          pos += charAt(pos + 1) === 'P' ? 2 : 1;
        }
        break;

      case 'Q':
        primary += 'K';
        alternate += 'K';
        pos += charAt(pos + 1) === 'Q' ? 2 : 1;
        break;

      case 'R':
        primary += 'R';
        alternate += 'R';
        pos += charAt(pos + 1) === 'R' ? 2 : 1;
        break;

      case 'S':
        if (charAt(pos + 1) === 'H') {
          primary += 'X';
          alternate += 'X';
          pos += 2;
        } else if (charAt(pos + 1) === 'C' && ['I', 'E', 'Y'].includes(charAt(pos + 2))) {
          primary += 'S';
          alternate += 'S';
          pos += 3;
        } else {
          primary += 'S';
          alternate += 'S';
          pos += charAt(pos + 1) === 'S' ? 2 : 1;
        }
        break;

      case 'T':
        if (charAt(pos + 1) === 'H') {
          primary += '0';
          alternate += 'T';
          pos += 2;
        } else {
          primary += 'T';
          alternate += 'T';
          pos += charAt(pos + 1) === 'T' ? 2 : 1;
        }
        break;

      case 'V':
        primary += 'F';
        alternate += 'F';
        pos += charAt(pos + 1) === 'V' ? 2 : 1;
        break;

      case 'W':
      case 'Y':
        if (isVowel(charAt(pos + 1))) {
          primary += ch;
          alternate += ch;
        }
        pos++;
        break;

      case 'X':
        primary += 'KS';
        alternate += 'KS';
        pos += charAt(pos + 1) === 'X' ? 2 : 1;
        break;

      case 'Z':
        primary += 'S';
        alternate += 'S';
        pos += charAt(pos + 1) === 'Z' ? 2 : 1;
        break;

      // Handle accented characters common in European languages
      case '\u00C7': // Ç
      case '\u00E7': // ç
        primary += 'S';
        alternate += 'S';
        pos++;
        break;

      case '\u00D1': // Ñ
      case '\u00F1': // ñ
        primary += 'N';
        alternate += 'N';
        pos++;
        break;

      default:
        pos++;
        break;
    }
  }

  return [primary.slice(0, 4), alternate.slice(0, 4)];
}

// ============================================================
// Levenshtein Distance
// ============================================================

/**
 * Calculate Levenshtein distance between two strings.
 * Returns the minimum number of single-character edits.
 */
export function levenshteinDistance(a: string, b: string): number {
  const aLen = a.length;
  const bLen = b.length;

  if (aLen === 0) return bLen;
  if (bLen === 0) return aLen;

  const matrix: number[][] = [];

  for (let i = 0; i <= aLen; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= bLen; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= aLen; i++) {
    for (let j = 1; j <= bLen; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[aLen][bLen];
}

/**
 * Calculate normalized string similarity (0 to 1).
 * 1 = identical, 0 = completely different.
 */
export function stringSimilarity(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length === 0 || b.length === 0) return 0;

  const distance = levenshteinDistance(a.toLowerCase(), b.toLowerCase());
  const maxLen = Math.max(a.length, b.length);
  return 1 - distance / maxLen;
}

// ============================================================
// Phonetic Similarity
// ============================================================

/**
 * Calculate phonetic similarity between two words using Double Metaphone.
 * Returns a value from 0 to 1.
 */
/**
 * Whether a word is written in the Latin alphabet (A-Z, plus common accented
 * Latin letters). Double Metaphone only understands Latin script; for other
 * scripts (Greek, Cyrillic, etc.) it produces empty/garbage codes.
 */
export function isLatinScript(word: string): boolean {
  // Strip combining accents, then check every letter is basic Latin A-Z.
  const stripped = word
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // combining diacritics
    .toLowerCase();
  // Consider it Latin if at least one A-Z letter and no non-Latin letters.
  const hasLatin = /[a-z]/.test(stripped);
  const hasNonLatinLetter = /[^\x00-\x7f]/.test(stripped.replace(/[^\p{L}]/gu, ''));
  return hasLatin && !hasNonLatinLetter;
}

export function phoneticSimilarity(expected: string, spoken: string): number {
  // Double Metaphone is Latin-only. For non-Latin scripts (e.g. Greek), fall
  // back to direct string similarity so a correct transcript isn't zeroed out.
  if (!isLatinScript(expected) || !isLatinScript(spoken)) {
    return stringSimilarity(expected, spoken);
  }

  const [expectedPrimary, expectedAlt] = doubleMetaphone(expected);
  const [spokenPrimary, spokenAlt] = doubleMetaphone(spoken);

  // If metaphone couldn't encode either side, fall back to string similarity
  // rather than returning 0 (which would wrongly tank a correct answer).
  if (!expectedPrimary || !spokenPrimary) {
    return stringSimilarity(expected, spoken);
  }

  // Check all combinations for best match
  const similarities = [
    stringSimilarity(expectedPrimary, spokenPrimary),
    stringSimilarity(expectedPrimary, spokenAlt),
    stringSimilarity(expectedAlt, spokenPrimary),
    stringSimilarity(expectedAlt, spokenAlt),
  ];

  return Math.max(...similarities);
}

// ============================================================
// Main Scoring Function
// ============================================================

export interface PronunciationScore {
  /** Final score 0-100 */
  score: number;
  /**
   * Whether this score reflects a real accuracy measurement. True when the
   * browser provided usable recognition confidence (accent-graded). False when
   * confidence was unavailable/unreliable for this language — in that case the
   * result is recognition-only (right word = pass), NOT an accuracy grade, and
   * the UI should present it as recognized/not-recognized rather than a % .
   */
  graded: boolean;
  /** Whether the recognizer heard (approximately) the correct word */
  recognized: boolean;
  /** Phonetic similarity component (0-1) */
  phoneticScore: number;
  /** String similarity component (0-1) */
  stringScore: number;
  /** The text that was recognized */
  recognizedText: string;
  /** Feedback label based on score thresholds */
  feedback: PronunciationFeedback;
}

export type PronunciationFeedback = 'try-again' | 'good-effort' | 'great' | 'perfect';

export interface FeedbackConfig {
  label: string;
  color: string;
  bgColor: string;
  emoji: string;
}

export const FEEDBACK_CONFIG: Record<PronunciationFeedback, FeedbackConfig> = {
  'try-again': {
    label: 'Try Again',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    emoji: '🔄',
  },
  'good-effort': {
    label: 'Good Effort',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    emoji: '👍',
  },
  'great': {
    label: 'Great!',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    emoji: '🌟',
  },
  'perfect': {
    label: 'Perfect!',
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    emoji: '🏆',
  },
};

/**
 * Get feedback category based on score.
 */
export function getFeedback(score: number): PronunciationFeedback {
  if (score >= 91) return 'perfect';
  if (score >= 71) return 'great';
  if (score >= 41) return 'good-effort';
  return 'try-again';
}

export type Strictness = 'normal' | 'strict';

/**
 * Calculate pronunciation score by comparing recognized speech to expected word.
 *
 * Scoring philosophy (client-side Web Speech API):
 * - Word match (phonetic + string similarity) determines whether the RIGHT word
 *   was said. This is a prerequisite, not the whole score.
 * - The API's `confidence` reflects how cleanly/native-like the utterance was
 *   recognized. We treat it as a first-class multiplier so that saying the right
 *   word sloppily / with a heavy accent scores lower than a clean utterance.
 * - There is no "exact text = 100" shortcut: a perfect score requires BOTH a
 *   correct word AND high recognition confidence.
 *
 * NOTE: The Web Speech API cannot truly grade accent at the phoneme level, so
 * this is an approximation. It is meaningfully stricter than a pure text match,
 * but genuine accent assessment requires a cloud speech-assessment service.
 *
 * @param expectedWord - The word the player should have said
 * @param recognizedText - The text recognized by Web Speech API
 * @param confidence - Confidence value from Speech API (0-1). Undefined is
 *   treated as low confidence, since a missing signal shouldn't earn a top score.
 * @param strictness - 'normal' (default) or 'strict' for harder grading
 * @returns PronunciationScore with score 0-100, components, and feedback
 */
export function calculatePronunciationScore(
  expectedWord: string,
  recognizedText: string,
  confidence?: number,
  strictness: Strictness = 'normal'
): PronunciationScore {
  if (!recognizedText || recognizedText.trim().length === 0) {
    return {
      score: 0,
      graded: false,
      recognized: false,
      phoneticScore: 0,
      stringScore: 0,
      recognizedText: '',
      feedback: 'try-again',
    };
  }

  const expected = expectedWord.toLowerCase().trim();
  const spoken = recognizedText.toLowerCase().trim();

  // Similarity is measured against the FULL recognized transcript, not the single
  // best-matching word. Extra/incorrect words the recognizer heard should drag the
  // score down rather than being filtered out.
  const stringScore = stringSimilarity(expected, spoken);
  const phoneticScore = phoneticSimilarity(expected, spoken);

  // Word-match component: did they say (approximately) the right word?
  // Weight phonetic slightly higher than raw spelling.
  const wordMatch = phoneticScore * 0.6 + stringScore * 0.4; // 0..1

  // The Web Speech API's `confidence` is only meaningful on some browsers/
  // languages. For many (notably Greek and other non-English locales) it comes
  // back as 0 or a low CONSTANT regardless of how well the word was spoken —
  // which would pin every correct answer to the same score (e.g. always 67%).
  // Only fold confidence into the score when it looks informative.
  const confUsable =
    confidence !== undefined && confidence > 0.3;

  let combined: number;
  let exponent: number;

  if (confUsable) {
    // Confidence is trustworthy: use it as a strictness modulator so a
    // correct-but-accented attempt can't reach the top band on words alone.
    // normal: 65% word / 35% confidence; strict: 55% / 45%.
    const wordWeight = strictness === 'strict' ? 0.55 : 0.65;
    const confWeight = 1 - wordWeight;
    combined = wordMatch * wordWeight + confidence! * confWeight;
    exponent = strictness === 'strict' ? 1.6 : 1.25;
  } else {
    // Confidence is missing/unreliable: score on word match alone so a correct
    // answer is rewarded properly instead of being capped at a constant value.
    // A gentle curve keeps near-misses from topping out.
    combined = wordMatch;
    exponent = strictness === 'strict' ? 1.3 : 1.1;
  }

  // Apply a curve so mediocre attempts don't cluster near the top.
  combined = Math.pow(combined, exponent);

  // If the right word clearly wasn't said, cap the score hard regardless of
  // confidence (prevents "confident gibberish" from scoring well).
  if (wordMatch < 0.5) {
    combined = Math.min(combined, wordMatch); // e.g. 0.3 word match -> max 0.30
  }

  // Was the correct word recognized? (threshold matches the hard-cap above)
  const recognized = wordMatch >= 0.5;

  let score: number;
  if (confUsable) {
    // Real accuracy grade.
    score = Math.round(Math.max(0, Math.min(100, combined * 100)));
  } else {
    // Recognition-only: we can't measure accent for this language, so this is a
    // pass/fail. Per product decision (Option A), a recognized word earns full
    // credit; an unrecognized one earns none. The UI presents this as
    // recognized/not-recognized rather than a misleading accuracy %.
    score = recognized ? 100 : 0;
  }

  return {
    score,
    graded: confUsable,
    recognized,
    phoneticScore,
    stringScore,
    recognizedText: spoken,
    feedback: getFeedback(score),
  };
}

/**
 * BCP 47 language codes for Web Speech API.
 * Maps our internal language codes to proper speech recognition locale.
 */
export const SPEECH_RECOGNITION_LOCALES: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  de: 'de-DE',
  pt: 'pt-BR',
  el: 'el-GR',
};

/**
 * Languages with potentially lower recognition accuracy.
 */
export const LOW_ACCURACY_LANGUAGES = new Set(['el', 'pt']);

/**
 * Check if the browser supports Speech Recognition.
 */
export function isSpeechRecognitionSupported(): boolean {
  return !!(
    (window as any).SpeechRecognition ||
    (window as any).webkitSpeechRecognition
  );
}
